import { API_BASE, axiosApi } from "../config"
import {
    BillingType,
    GeneralReturnInterface,
    GetOrdersParamType,
    GetOrdersReturnInterface,
    OrderCommentType,
    OrderEmailLogInterface,
    OrderInfoInterface,
    OrderStatusType,
    SubmitOrderReturnInterface
} from "../types"

export const updateOrderStatus = async (body: { orderId: number,newStatus:OrderStatusType }): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/updateOrderStatus`, body)).data
}

export const completeRunIfSingleOrder = async (body: { orderId: number }): Promise<GeneralReturnInterface & {
    updated?: boolean
    runId?: number
    reason?: string
    runIds?: number[]
    results?: { runId: number; updated: boolean; reason: string }[]
}> => {
    return (await axiosApi.post(`${API_BASE}/completeRunIfSingleOrder`, body)).data
}

export const createInvoice = async (body: {
    orderIds: number[]
    invoiceDate: string
    dueDate: string
    billingContact: string
    billingEmail: string
    billingAddress: string
    institution: string
    discount?: number
    notes?: string
}): Promise<GeneralReturnInterface & { invoiceId?: number }> => {
    return (await axiosApi.post(`${API_BASE}/createInvoice`, body)).data
}

export const submitOrder = async (body: { orderInfo: OrderInfoInterface }): Promise<SubmitOrderReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/submitOrder`, body)).data
}

export const getOrders = async (body: {
    type: GetOrdersParamType
    orderIds?: number[]
    hideGroupOrders?: boolean
    page?: number
    pageSize?: number
    showCanceled?: boolean
}): Promise<GetOrdersReturnInterface> => {
    const data = (await axiosApi.post(`${API_BASE}/getOrders`, body)).data
    return data
}

export const setOrderShippingStatus = async (body: {
    orderId: number
    shipped: boolean
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/setOrderShippingStatus`, body)).data
}


export const addOrderComment = async (body: {
    orderId: number
    content: string
    type: OrderCommentType
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/addOrderComment`, body)).data
}
export const getOrderEmailLog = async (body: {
    orderId: number
}): Promise<{ code: number, message: string, emails: OrderEmailLogInterface[] }> => {
    return (await axiosApi.post(`${API_BASE}/getOrderEmailLog`, body)).data
}
export const sendResultReadyEmail = async (body: {
    orderId: number
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/sendResultReadyEmail`, body)).data
}

export const updateOrderPaidStatus = async (body: {
    orderId: number
    paid: boolean
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/updateOrderPaidStatus`, body)).data
}

export const updateOrderItrPurityAnalysis = async (body: {
    orderId: number
    itrPurityAnalysis: boolean
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/updateOrderItrPurityAnalysis`, body)).data
}

export const updateOrderSubmissionType = async (body: {
    orderId: number
    submissionType: string
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/updateOrderSubmissionType`, body)).data
}

export const updateOrderBillingType = async (body: {
    orderId: number
    billingType: BillingType
}): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/updateOrderBillingType`, body)).data
}

export interface OrderResultDownloadLog {
    id: number
    userid: number
    username: string
    ts: string
}

export const getOrderResultDownloadLogs = async (body: { orderId: number }): Promise<{ code: number, message: string, downloads: OrderResultDownloadLog[] }> => {
    return (await axiosApi.post(`${API_BASE}/getOrderResultDownloadLogs`, body)).data
}

export interface InvoiceDownloadLog {
    id: number
    userid: number
    username: string
    ts: string
    action: string
}

export const getInvoiceDownloadLogs = async (body: { orderId: number }): Promise<{ code: number, message: string, downloads: InvoiceDownloadLog[] }> => {
    return (await axiosApi.post(`${API_BASE}/getInvoiceDownloadLogs`, body)).data
}

export const deleteInvoicesForOrders = async (body: { orderIds: number[] }): Promise<GeneralReturnInterface & { 
    deletedInvoiceIds?: number[], 
    warnings?: Array<{
        invoiceId: number,
        unselectedOrderIds: number[],
        message: string
    }>
}> => {
    return (await axiosApi.post(`${API_BASE}/deleteInvoicesForOrders`, body)).data
}

export interface OrderResultInterface {
    id: number
    orderid: number
    jsonfile: string
    zipfile: string
    pin_for_download?: boolean
    zipfilesize?: number | null
    pushed_to_aws_for_group: number | null
    released_to_restapi_for_group: number | null
    pushed_group_name?: string | null
    released_group_name?: string | null
    notes?: string
    ts: string
}

export interface OrderResultJsonDiffValue {
    orderResultId: number
    orderId: number
    jsonfile: string
    value: unknown
}

export interface OrderResultJsonDifference {
    path: string
    values: OrderResultJsonDiffValue[]
}

export const getOrderResults = async (body: { orderId: number }): Promise<{ 
    code: number, 
    message: string, 
    results: OrderResultInterface[] 
}> => {
    return (await axiosApi.post(`${API_BASE}/getOrderResults`, body)).data
}

export const releaseOrderResults = async (body: { orderResultId: number }): Promise<{
    code: number,
    message: string,
    details?: {
        orderResultId: number,
        awsPushed: boolean,
        apiReleased: boolean,
        awsRequired: boolean,
        apiRequired: boolean,
        errors: string[]
    }
}> => {
    return (await axiosApi.post(`${API_BASE}/releaseOrderResults`, body)).data
}

export const regenerateOrderSheet = async (body: { orderId: number }): Promise<GeneralReturnInterface> => {
    return (await axiosApi.post(`${API_BASE}/regenerate/ordersheet/${body.orderId}`, {})).data
}

export const downloadOrderResultJson = async (orderResultId: number): Promise<void> => {
    const response = await axiosApi.get(`${API_BASE}/download/orderresult/json/${orderResultId}`, {
        responseType: 'blob'
    })
    
    // Create blob link to download file
    const blob = new Blob([response.data], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Try to get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition']
    let filename = `order_result_${orderResultId}.json`
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
            filename = filenameMatch[1]
        }
    }
    
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
}

export const downloadOrderResultZip = async (orderResultId: number): Promise<void> => {
    const response = await axiosApi.get(`${API_BASE}/download/orderresult/zip/${orderResultId}`, {
        responseType: 'blob'
    })
    
    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition']
    let filename = `order_result_${orderResultId}.zip`
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/)
        if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '')
        }
    }
    
    // Create blob and download
    const blob = new Blob([response.data], { type: 'application/zip' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
}

export const deleteOrderResult = async (body: { orderResultId: number }): Promise<GeneralReturnInterface & {
    details: {
        orderResultId: number,
        orderId: number,
        deletedFiles: number,
        failedDeletes: number,
        errors?: string
    }
}> => {
    return (await axiosApi.post(`${API_BASE}/deleteOrderResult`, body)).data
}

export const batchDeleteOrderResults = async (body: { orderResultIds: number[] }): Promise<GeneralReturnInterface & {
    details: {
        totalRequested: number,
        successfulDeletes: number,
        failedDeletes: number,
        deletedFiles: number,
        errors?: string[]
    }
}> => {
    return (await axiosApi.post(`${API_BASE}/batchDeleteOrderResults`, body)).data
}

export const updateOrderResult = async (body: { orderResultId: number, notes?: string }): Promise<GeneralReturnInterface & {
    orderResultId: number
}> => {
    return (await axiosApi.post(`${API_BASE}/updateOrderResult`, body)).data
}

export const setPinnedOrderResult = async (body: { orderResultId: number }): Promise<GeneralReturnInterface & {
    details?: {
        orderResultId: number
        orderId: number
    }
}> => {
    return (await axiosApi.post(`${API_BASE}/setPinnedOrderResult`, body)).data
}

export const cleanOrphanedResultFiles = async (): Promise<GeneralReturnInterface & {
    details: {
        totalFiles: number,
        orphanedFiles: number,
        deletedFiles: number,
        errors?: string[]
    }
}> => {
    return (await axiosApi.post(`${API_BASE}/cleanOrphanedResultFiles`, {})).data
}

export const compareOrderResultJsons = async (body: { orderResultIds: number[] }): Promise<{
    code: number
    message: string
    compared: Array<{
        orderResultId: number
        orderId: number
        jsonfile: string
        sampleMappings: Array<{
            sampleIndex: number
            sampleId: number | null
            sampleName: string
            barcodePath: string
        }>
    }>
    maxDifferences: number
    truncated: boolean
    differences: OrderResultJsonDifference[]
}> => {
    return (await axiosApi.post(`${API_BASE}/compareOrderResultJsons`, body)).data
}
